import { useState, useEffect } from "react";
import { Grid, X, Maximize, Minimize, Radio } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const MultiViewPage = () => {
  const [allGames, setAllGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [layout, setLayout] = useState(2);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const [liveRes, schedRes] = await Promise.all([
          axios.get(`${API}/broadcasts/live`),
          axios.get(`${API}/broadcasts/schedule?upcoming_only=true`)
        ]);
        const live = (liveRes.data || []).map(g => ({ ...g, is_live: true }));
        const upcoming = (schedRes.data || []).filter(g => g.youtube_url || g.vcloud_url);
        const combined = [...live, ...upcoming.filter(u => !live.find(l => l.id === u.id))];
        setAllGames(combined);
        setSelectedGames(live.slice(0, 2));
      } catch (err) {
        console.error("Error fetching games:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
    const interval = setInterval(fetchGames, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleGame = (game) => {
    if (selectedGames.find(g => g.id === game.id)) {
      setSelectedGames(selectedGames.filter(g => g.id !== game.id));
    } else if (selectedGames.length < layout) {
      setSelectedGames([...selectedGames, game]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-zinc-500 animate-pulse">Loading streams...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-4 py-6" data-testid="multiview-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Grid className="w-6 h-6 text-orange-500" />
            MULTI-VIEW
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 text-sm">Layout:</span>
            {[2, 4].map(n => (
              <button
                key={n}
                onClick={() => setLayout(n)}
                className={`px-3 py-1 text-sm font-medium transition-colors ${
                  layout === n ? "bg-orange-500 text-black" : "bg-zinc-800 text-white hover:bg-zinc-700"
                }`}
                data-testid={`layout-${n}-btn`}
              >
                {n} Games
              </button>
            ))}
          </div>
        </div>

        {/* Video Grid */}
        <div className={`grid gap-3 mb-6 ${layout === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2"}`}>
          {selectedGames.slice(0, layout).map((game) => (
            <div key={game.id} className="relative bg-zinc-900 overflow-hidden" data-testid={`multiview-slot-${game.id}`}>
              <button
                onClick={() => toggleGame(game)}
                className="absolute top-2 right-2 z-10 p-1 bg-black/60 rounded text-white hover:bg-red-600 transition-colors"
                data-testid={`remove-game-${game.id}`}
              >
                <X className="w-4 h-4" />
              </button>
              {game.is_live && (
                <span className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold uppercase flex items-center gap-1">
                  <Radio className="w-3 h-3" /> LIVE
                </span>
              )}
              <div className="aspect-video">
                {game.vcloud_url ? (
                  <iframe src={game.vcloud_url} className="w-full h-full border-0" allowFullScreen title={game.title} />
                ) : game.youtube_url ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(game.youtube_url)}?autoplay=1&mute=1`}
                    className="w-full h-full border-0"
                    allowFullScreen
                    allow="autoplay"
                    title={game.title}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500">
                    No stream available
                  </div>
                )}
              </div>
              <div className="p-2 bg-zinc-900/80">
                <p className="text-white text-sm font-medium truncate">{game.title}</p>
                <p className="text-zinc-500 text-xs">{game.sport}</p>
              </div>
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: Math.max(0, layout - selectedGames.length) }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-video bg-zinc-900/50 flex items-center justify-center border-2 border-dashed border-white/10">
              <p className="text-zinc-600 text-sm">Select a game below</p>
            </div>
          ))}
        </div>

        {/* Game Selector */}
        <div>
          <h3 className="text-base font-bold text-white mb-3 uppercase tracking-wider">Available Games</h3>
          {allGames.length === 0 ? (
            <p className="text-zinc-500 text-sm">No live or upcoming games with streams available.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {allGames.map((game) => {
                const isSelected = selectedGames.find(g => g.id === game.id);
                return (
                  <button
                    key={game.id}
                    onClick={() => toggleGame(game)}
                    disabled={!isSelected && selectedGames.length >= layout}
                    className={`p-3 text-left transition-all ${
                      isSelected
                        ? "bg-orange-500/20 border-2 border-orange-500"
                        : selectedGames.length >= layout
                          ? "bg-zinc-900 border border-white/5 opacity-40 cursor-not-allowed"
                          : "bg-zinc-900 border border-white/10 hover:border-orange-500/50"
                    }`}
                    data-testid={`game-select-${game.id}`}
                  >
                    <p className="text-white text-sm font-medium truncate">{game.title}</p>
                    <p className="text-zinc-500 text-xs">{game.sport}</p>
                    {game.is_live && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold uppercase">LIVE</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiViewPage;
